import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { FullStoryExclude, FullStoryMask } from '@calendly/ui/components/fullstory-privacy';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextDestructive, colorTextSecondary, colorTextWarning } from '@calendly/ui/theme';
import { RemoteContactsSearchHandler } from 'features/crm';
import { useTrack } from 'hooks/analytics_events';
import { getInitials } from 'shared/utils';
import { AttendeeDetailsInline } from './attendee_details_inline';
import { useResolvedAttendeeDetails } from './use_resolved_attendee_details';
import CancelledIcon from '../cancel.svg';
import ConfirmedIcon from '../confirmed.svg';
import { EditEmailModal } from '../edit_email_modal';
import { InviteeActions } from '../invitee_actions';
import { InviteeDetails } from '../invitee_details';
import { NoShowBanner } from '../no_show_banner';
import { getPaymentInfo, getPaymentSubtitleText } from '../payment_utils';
import RescheduledIcon from '../reschedule.svg';
import { Wrapper, Container, AvatarContainer, AvatarIcon, NameString, NameContainer, wrapperVerticalPaddingOnlyCss, avatarContainerFixedSizeCss, containerSoloAttendeeHeaderCss } from '../styles';
import { useEventMeetingPackageContactRow } from '../use_event_meeting_package_contact_row';
import { useInviteePermissions } from '../use_invitee_permissions';
const expandableNameCss = "ey5ls25";
const nameContainerExpandableCss = "n11ogclq";
const chevronClusterCss = "c22lbvy";
const soloActionsRowCss = "s11l9nqy";
export const BaseAttendeeCard = ({
  event,
  invitee,
  readonly,
  variant = 'inline',
  isExpanded,
  onToggle
}) => {
  const {
    resolved,
    isLoading,
    handleContactResults,
    handleContactFetching
  } = useResolvedAttendeeDetails(invitee);
  const {
    packageInfo,
    viewPackage
  } = useEventMeetingPackageContactRow(event, resolved);
  const [noShowClicked, turnOnNoShowClicked] = useBooleanState(false);
  const [isEditEmailModalOpen, openEditEmailModal, closeEditEmailModal] = useBooleanState(false);
  const track = useTrack();
  const sendAnalytics = (action, contactUuid) => {
    track({
      name: 'Contact Interaction',
      properties: {
        action: action || 'profile_view',
        location: 'meetings_page_right_rail',
        contact_uuid: contactUuid || invitee.uuid
      }
    });
  };
  const {
    isCancelled,
    isRescheduled,
    isConfirmed,
    canMarkNoShow,
    canEditEmail: canEditEmailBase,
    canReschedule,
    canCancel,
    canBlock,
    canUnblock,
    noShow,
    blockedByHost
  } = useInviteePermissions(event, invitee, readonly);
  const canEditEmail = canEditEmailBase && !blockedByHost;
  const showNoShowBanner = canMarkNoShow && noShowClicked;
  const contactInfoForDetails = resolved ? {
    contactUuid: resolved.contactUuid,
    contactId: resolved.contactId,
    email: resolved.email,
    name: resolved.name,
    phone: resolved.phone,
    timezone: resolved.timezone
  } : {};
  const isExpandable = variant === 'expandable';
  const {
    charge
  } = invitee;
  const hasCharge = charge && charge.captured;
  const showCharge = isExpandable && hasCharge;
  const questionsCount = (invitee.eventFields?.length ?? 0) + (invitee.formBuilderSubmission?.questions?.length ?? 0);
  const paymentSubtitleText = hasCharge && charge ? getPaymentSubtitleText(charge) : null;
  const handleHeaderKeyDown = e => {
    if (isExpandable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggle?.();
    }
  };
  const cardContent = !isLoading && resolved && <>
      {isExpandable ? <Container isGroup isOpen={isExpanded} noBottomMargin onClick={onToggle} onKeyDown={handleHeaderKeyDown} aria-expanded={isExpanded} role="button" tabIndex={0}>
          <AvatarContainer className={avatarContainerFixedSizeCss}>
            <FullStoryExclude>
              <Avatar letter={getInitials(resolved.name)} src={resolved.avatarUrl} alt={resolved.name} aria-label={resolved.name} size="small" />
              {(isCancelled || blockedByHost) && <AvatarIcon src={CancelledIcon} />}
              {isRescheduled && <AvatarIcon src={RescheduledIcon} />}
              {isConfirmed && <AvatarIcon src={ConfirmedIcon} />}
            </FullStoryExclude>
          </AvatarContainer>
          <NameContainer className={nameContainerExpandableCss}>
            <NameString strike={isCancelled || isRescheduled || blockedByHost} className={expandableNameCss}>
              <FullStoryMask>{resolved.name}</FullStoryMask>
            </NameString>
            {isExpandable && isRescheduled && <BodyText size="x-small" color={colorTextWarning} weight="semibold">
                {I18n.t('publisher.meetings.header_badge.rescheduled')}
              </BodyText>}
            {isExpandable && isCancelled && !isRescheduled && !blockedByHost && <BodyText size="x-small" color={colorTextDestructive} weight="semibold">
                {I18n.t('publisher.meetings.header_badge.canceled')}
              </BodyText>}
            {isExpandable && blockedByHost && <BodyText size="x-small" color={colorTextDestructive} weight="semibold">
                {I18n.t('publisher.meetings.invitees.blocked')}
              </BodyText>}
            {isExpandable && noShow && !blockedByHost && !isRescheduled && !isCancelled && <BodyText size="x-small" color={colorTextDestructive} weight="semibold">
                {I18n.t('publisher.meetings.header_badge.no_show')}
              </BodyText>}
            {isExpandable && !isRescheduled && !isCancelled && !blockedByHost && !noShow && (questionsCount > 0 || showCharge) && <BodyText size="x-small" color={colorTextSecondary}>
                {questionsCount > 0 && showCharge && <>
                    {I18n.t('publisher.meetings.invitees.questions_answered_count', {
              count: questionsCount
            })}
                    {' • '}
                    {paymentSubtitleText}
                  </>}
                {questionsCount > 0 && !showCharge && I18n.t('publisher.meetings.invitees.questions_answered_count', {
            count: questionsCount
          })}
                {!questionsCount && showCharge && paymentSubtitleText}
              </BodyText>}
          </NameContainer>
          <span className={chevronClusterCss}>
            <BodyText size="x-small" color={colorTextSecondary} as="span">
              {I18n.t('publisher.meetings.invitees.detail')}
            </BodyText>
            {isExpanded ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
          </span>
        </Container> : <Container className={containerSoloAttendeeHeaderCss}>
          <AvatarContainer className={avatarContainerFixedSizeCss}>
            <FullStoryExclude>
              <Avatar letter={getInitials(resolved.name)} src={resolved.avatarUrl} alt={resolved.name} aria-label={resolved.name} size="small" />
              {(isCancelled || blockedByHost) && <AvatarIcon src={CancelledIcon} />}
              {isRescheduled && <AvatarIcon src={RescheduledIcon} />}
              {isConfirmed && <AvatarIcon src={ConfirmedIcon} />}
            </FullStoryExclude>
          </AvatarContainer>
          <NameContainer>
            <NameString strike={isCancelled || isRescheduled || blockedByHost}>
              <FullStoryMask>{resolved.name}</FullStoryMask>
            </NameString>
            {blockedByHost && <BodyText size="x-small" color={colorTextDestructive} weight="semibold">
                {I18n.t('publisher.meetings.invitees.blocked')}
              </BodyText>}
            {noShow && !blockedByHost && <BodyText size="x-small" color={colorTextDestructive} weight="semibold">
                {I18n.t('publisher.meetings.header_badge.no_show')}
              </BodyText>}
            {hasCharge && paymentSubtitleText && <BodyText size="x-small" color={colorTextSecondary}>
                {paymentSubtitleText}
              </BodyText>}
          </NameContainer>
        </Container>}

      {isExpandable ? isExpanded && <InviteeDetails event={event} invitee={invitee} readonly={readonly} afterExpandableHeader openEditEmailModal={openEditEmailModal} canMarkNoShow={canMarkNoShow} turnOnNoShowClicked={turnOnNoShowClicked} canReschedule={canReschedule} canCancel={canCancel} canEditEmail={canEditEmail} canBlock={canBlock} canUnblock={canUnblock} contactInfo={contactInfoForDetails} sendAnalytics={sendAnalytics} packageInfo={packageInfo} viewPackage={viewPackage} /> : <>
            <AttendeeDetailsInline resolved={resolved} canEditEmail={canEditEmail} openEditEmailModal={openEditEmailModal} paymentInfo={hasCharge ? getPaymentInfo(charge, hasCharge) : undefined} paymentLink={hasCharge && charge?.externalChargeLink ? charge.externalChargeLink : undefined} />
            <div className={soloActionsRowCss}>
              <InviteeActions event={event} invitee={invitee} resolved={resolved} canMarkNoShow={canMarkNoShow} onMarkAsNoShow={turnOnNoShowClicked} sendAnalytics={sendAnalytics} showPrimaryActions canReschedule={canReschedule} canCancel={canCancel} canBlock={canBlock} canUnblock={canUnblock} canEditEmail={canEditEmail} openEditEmailModal={openEditEmailModal} />
            </div>
          </>}
    </>;
  return <>
      <Wrapper isGroup={isExpandable} isExpanded={isExpandable && isExpanded} className={!isExpandable ? wrapperVerticalPaddingOnlyCss : undefined} data-testid={isExpandable ? `invitee-${invitee.uuid ?? invitee.id}` : `solo-attendee-${invitee.uuid}`}>
        <FullStoryExclude>
          <RemoteContactsSearchHandler emails={invitee.email ? [invitee.email] : undefined} onResults={handleContactResults} onFetching={handleContactFetching} />
        </FullStoryExclude>

        {isLoading && <Loader data-testid="attendee-loader" size="small" />}

        {cardContent}

        {showNoShowBanner && <NoShowBanner />}
      </Wrapper>

      {isEditEmailModalOpen && <EditEmailModal invitee={invitee} onClose={closeEditEmailModal} />}
    </>;
};

require("./base_attendee_card.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./base_attendee_card.js");